import { useEffect, useState } from 'react'
import StyledButton from '../../generic/StyledButton'
import DraggableModal from '../../generic/DraggableModal'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import Grid from '@mui/material/Unstable_Grid2'
import { toPrice } from '../../../helpers/stringFormat'
import Checkbox from '@mui/material/Checkbox'
import FormGroup from '@mui/material/FormGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import { backendGetRequest, backendPatchWithoutRefresh } from '../../../helpers/callBackend'
import TermsAndConditionsCopy from './TermsAndConditionsCopy'
import { useUcs } from '../../../helpers/contexts/UserCompanySettingsContext'
import { errorMessage } from '../../../helpers/errorMessageFunc'
import checkFlag from '../../../helpers/featureFlags'
import PermissionGate from '../../../helpers/PermissionGate'
import GenerateCodeV2 from '../../formItems/GenerateCodeV2'

// an example of custom error functions on frontend
class BackendResponseError extends Error {
  constructor(response, message = `Unexpected status code: ${response.status}`) {
    super(message)
    this.name = 'HttpResponseError'
    this.status = response.status
    this.statusText = response.statusText
    this.url = response.url
    this.response = response
  }
}

const ApprovePaymentModal = ({ visible, handleCancelModal, paymentRequest, updatePaymentRequests }) => {
  const [amount, setAmount] = useState(paymentRequest.amount || 0)
  const [total, setTotal] = useState(paymentRequest.amount)
  const [method, setMethod] = useState(paymentRequest.method ? paymentRequest.method : 'ACH')
  const [counterPartyName, setCounterPartyName] = useState(
    paymentRequest.counterparty_name ? paymentRequest.counterparty_name : 'Unknown'
  )
  const [tAndCApproved, setTAndCApproved] = useState(false)
  const [feeApproved, setFeeApproved] = useState(false)
  const [fees, setFees] = useState([])
  const [gotFees, setGotFees] = useState(false)
  const [feesRequired, setFeesRequired] = useState(false)
  const [verificationToken, setVerificationToken] = useState('')
  const [otp, setOtp] = useState('')
  const [userCanApprove, setUserCanApprove] = useState(false)
  const [userLimitMessage, setUserLimitMessage] = useState('')

  const ucsContext = useUcs()

  useEffect(() => {
    if (ucsContext.loaded) {
      let limit = 'approve'
      if (paymentRequest.created_by == ucsContext.userName()) {
        limit = 'self-approve'
        setUserLimitMessage(`$${amount} exceeds your self-approval limit of $${ucsContext.approveLimit()}`)
      } else {
        setUserLimitMessage(`$${amount} exceeds your approval limit of $${ucsContext.selfApproveLimit()}`)
      }
      setUserCanApprove(ucsContext.checkLimit(limit, amount))
    }
  }, [ucsContext])

  const getFees = async () => {
    try {
      const feesResp = await backendGetRequest('/api/arpari-dwolla/fees', {
        params: {
          amount: amount,
        },
      })
      if (feesResp.status != 200) {
        throw new BackendResponseError(feesResp)
      }
      if (feesResp.data?.data.length > 0) {
        setFeesRequired(true)
        setFees(feesResp?.data?.data)
      } else {
        // set fees as approved if there are no fees
        setFeeApproved(true)
      }
    } catch (error) {
      console.error(error)
      setFees([])
    }
    setGotFees(true)
  }

  const handleApprove = async () => {
    if (!tAndCApproved) {
      errorMessage('Please approve terms and conditions')
      return
    }
    if (!feeApproved && feesRequired) {
      errorMessage('Please approve service fees')
      return
    }
    if (checkFlag('otpPayments') && !otp) {
      errorMessage('Please enter a one time passcode')
      return
    }
    if (!ucsContext.loaded) {
      errorMessage('Unable to load user permissions for payment request approval')
      return
    }
    if (paymentRequest.created_by == ucsContext.userName()) {
      // user created this payment request
      if (!ucsContext.checkLimit('self-approve', amount)) {
        errorMessage(`$${amount} exceeds your self-approval limit of $${ucsContext.selfApproveLimit()}`)
        return
      }
    } else {
      // user trying to approve team member payment request
      if (!ucsContext.checkLimit('approve', amount)) {
        errorMessage(
          `$${amount} exceeds your approval limit of $${ucsContext.approveLimit()} for team member payment requests.`
        )
        return
      }
    }
    if (paymentRequest.available_balance < amount) {
      errorMessage('Insufficient funds. Amount exceeds the available balance.')
      return
    }

    const data = {
      payment_request_id: paymentRequest.id,
      add_approver: true,
      otp: otp,
      verificationToken: verificationToken,
    }
    try {
      const patchResp = await backendPatchWithoutRefresh('/api/payments/payment-request/', data)
      if (patchResp.status != 200) {
        throw new BackendResponseError(patchResp)
      }
      updatePaymentRequests(patchResp.data?.data, 'replace')
      handleCancelModal(false)
    } catch (error) {
      errorMessage('Error processing approval')
    }
  }

  useEffect(() => {
    setAmount(paymentRequest.amount)
    setMethod(paymentRequest.method ? paymentRequest.method : 'ACH')
    setCounterPartyName(paymentRequest.counterparty_name ? paymentRequest.counterparty_name : 'Unknown')
    getFees()
  }, [paymentRequest])

  useEffect(() => {
    let sumFees = 0
    if (fees) {
      fees.forEach((fee) => {
        sumFees = sumFees + parseFloat(fee.amount)
      })
    }
    setTotal(parseFloat(paymentRequest.amount) + sumFees)
  }, [amount, fees])

  return (
    <DraggableModal
      modalTitle='Approve Payment'
      modalVisible={visible && gotFees}
      handleCancelModal={handleCancelModal}
      width='50%'
      content={
        <Stack spacing={1}>
          <Typography>{`Are you sure you want to approve ${method} to ${counterPartyName}?`}</Typography>
          <Grid container spacing={5} sx={{ alignItems: 'center' }}>
            <Grid direction='row' spacing={5}>
              <Typography>Amount</Typography>
              {fees &&
                fees.map((fee) => {
                  return <Typography key={fee.name}>{fee.name}</Typography>
                })}
              <Typography>Total</Typography>
            </Grid>
            <Grid direction='row'>
              <Typography>{toPrice(amount)}</Typography>
              {fees &&
                fees.map((fee) => {
                  return <Typography key={fee.amount}>{toPrice(fee.amount)}</Typography>
                })}
              <Typography>{toPrice(total)}</Typography>
            </Grid>
          </Grid>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox defaultChecked={tAndCApproved} onChange={(event) => setTAndCApproved(event.target.checked)} />
              }
              label={<TermsAndConditionsCopy />}
            />
            {feesRequired && (
              <FormControlLabel
                control={
                  <Checkbox defaultChecked={feeApproved} onChange={(event) => setFeeApproved(event.target.checked)} />
                }
                label='By checking this box you acknowledge the above facillitator fee will be applied to this payment.'
              />
            )}
          </FormGroup>
          {checkFlag('otpPayments') && <GenerateCodeV2 setOtp={setOtp} />}
        </Stack>
      }
      actions={
        <Stack direction='row' spacing={3} width='100%'>
          <PermissionGate
            condition={userCanApprove}
            readOnly={<StyledButton label='Yes' fontSize={14} disabled={true} customTheme='outlined' />}
            toolTipText={userLimitMessage}
            toolTipPlacement='top'
            spanSettings={{ style: { width: '100%' } }}
          >
            {feeApproved && tAndCApproved ? (
              <StyledButton label='Yes' fontSize={14} onClick={handleApprove} />
            ) : (
              <StyledButton label='Yes' fontSize={14} disabled={true} customTheme='outlined' />
            )}
          </PermissionGate>
          <StyledButton
            label='No'
            customTheme='outlined'
            fontSize={14}
            onClick={() => {
              setTAndCApproved(false)
              setFeeApproved(false)
              handleCancelModal(false)
            }}
          />
        </Stack>
      }
    />
  )
}

export default ApprovePaymentModal
